/**
 * Google Analytics 4 helpers.
 *
 * The bootstrap in index.html sets send_page_view:false, so route changes are
 * fired manually in main.tsx via router.subscribe — exactly one page_view per
 * route change.
 *
 * Keep in sync with apps/partner-portal/src/lib/analytics.ts and
 * apps/landing/src/lib/analytics.ts — no shared package yet.
 */

// Only the shapes invoked from TypeScript. 'config', 'consent', and 'js' are
// emitted by the inline bootstrap in index.html, which bypasses TS.
type GtagArgs =
  | ['event', string, Record<string, unknown>?]
  | ['set', Record<string, unknown>];

declare global {
  interface Window {
    gtag?: (...args: GtagArgs) => void;
  }
}

const GA_ID_PATTERN = /^G-[A-Z0-9]{6,}$/;
const rawGaId = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
export const GA_MEASUREMENT_ID =
  rawGaId && GA_ID_PATTERN.test(rawGaId) ? rawGaId : undefined;

export const gaEnabled = Boolean(GA_MEASUREMENT_ID);

function gtag(...args: GtagArgs): void {
  if (!gaEnabled || typeof window === 'undefined' || !window.gtag) return;
  window.gtag(...args);
}

/**
 * Fire a GA4 page_view for the given pathname.
 * Query strings are intentionally dropped — some routes carry single-use invite
 * tokens and payment references that must never leave the app.
 */
export function trackPageView(pathname: string): void {
  gtag('event', 'page_view', {
    page_path: pathname,
    page_location: window.location.origin + pathname,
  });
}

export function trackEvent(name: string, params?: Record<string, unknown>): void {
  gtag('event', name, params ?? {});
}

export function setUserId(userId: string | null): void {
  gtag('set', { user_id: userId });
}

export function setUserProperty(name: string, value: string): void {
  gtag('set', { [name]: value });
}

/**
 * GA4 recommended event: fires when a user completes registration.
 * Method values should describe the auth mechanism (e.g. 'google_oauth', 'email_otp').
 */
export function trackSignUp(method: string): void {
  trackEvent('sign_up', { method });
}

/**
 * GA4 recommended event: fires when a user signs in.
 */
export function trackLogin(method: string): void {
  trackEvent('login', { method });
}

// ---------------------------------------------------------------------------
// Loan Readiness Pack — one-shot purchase funnel (merchant PWA only)
// ---------------------------------------------------------------------------
//
// NOTE: never send the loan-pack `reference` to GA. It is a stable per-business
// financial-document identifier; pairing it with GA's client_id would link a
// Google-side profile to a merchant's loan data (NDPR data-minimisation). The
// funnel is measured by event counts + the non-identifying payment_status only.
// This mirrors trackPageView's rule that payment references must never leave the app.

/** A loan-pack preview rendered. `paymentStatus` distinguishes free preview from entitled. */
export function trackLoanPackPreviewed(paymentStatus: string): void {
  trackEvent('loan_pack_previewed', { payment_status: paymentStatus });
}

/** The owner clicked "Unlock" on a PENDING pack, starting checkout. */
export function trackLoanPackUnlockClicked(): void {
  trackEvent('loan_pack_unlock_clicked');
}

/** A one-shot payment was confirmed (callback verify succeeded). */
export function trackLoanPackPurchased(): void {
  trackEvent('loan_pack_purchased');
}

/** The PDF was downloaded (after unlock, or directly for INCLUDED/PAID packs). */
export function trackLoanPackDownloaded(paymentStatus: string): void {
  trackEvent('loan_pack_downloaded', { payment_status: paymentStatus });
}
